const getFilters = (types, tags, withExcludes = true) => {
  const filters = {};

  const fileTagOptions = (tags || [])
    .filter((tag) => tag.categories?.includes('files'))
    .map((tag) => ({ enum: [tag.id], title: tag.name }));
  const hasFileTagOptions = fileTagOptions.length > 0;

  if (types?.length) {
    filters.includeTypes = {
      title: '',
      type: 'object',
      properties: {
        active: {
          type: 'boolean',
          title: 'Restrict by file type (will disable filter).',
          default: false,
        },
      },
      dependencies: {
        active: {
          oneOf: [
            { properties: { active: { enum: [false] } } },
            {
              properties: {
                active: { enum: [true] },
                types: {
                  type: 'array',
                  title: 'File types',
                  default: [],
                  uniqueItems: true,
                  isMulti: true,
                  items: {
                    type: 'string',
                    anyOf: types.map((type) => ({ enum: [type], title: type })),
                  },
                },
              },
            },
          ],
        },
      },
    };
  }

  if (types?.length && withExcludes) {
    filters.excludeTypes = {
      title: '',
      type: 'object',
      properties: {
        active: {
          type: 'boolean',
          title: 'Specify file types to exclude (unchecked for none, overridden by includes).',
          default: false,
        },
      },
      dependencies: {
        active: {
          oneOf: [
            { properties: { active: { enum: [false] } } },
            {
              properties: {
                active: { enum: [true] },
                types: {
                  type: 'array',
                  title: 'File types',
                  default: [],
                  uniqueItems: true,
                  isMulti: true,
                  items: {
                    type: 'string',
                    anyOf: types.map((type) => ({ enum: [type], title: type })),
                  },
                },
              },
            },
          ],
        },
      },
    };
  }

  if (hasFileTagOptions) {
    filters.includeTags = {
      title: '',
      type: 'object',
      properties: {
        active: {
          type: 'boolean',
          title: 'Specify tags to include (unchecked for all).',
          default: false,
        },
      },
      dependencies: {
        active: {
          oneOf: [
            { properties: { active: { enum: [false] } } },
            {
              properties: {
                active: { enum: [true] },
                operator: {
                  type: 'string',
                  title: 'Filter type',
                  default: 'or',
                  oneOf: [
                    { enum: ['and'], title: 'All (files must include all of the selected tags)' },
                    {
                      enum: ['or'],
                      title:
                        'Any (files must include any of the selected tags, will disable filter)',
                    },
                  ],
                },
                tags: {
                  type: 'array',
                  title: 'Select tags for this filter.',
                  default: [],
                  uniqueItems: true,
                  isMulti: true,
                  items: {
                    type: 'string',
                    anyOf: fileTagOptions,
                  },
                },
              },
            },
          ],
        },
      },
    };
  }

  if (hasFileTagOptions && withExcludes) {
    filters.excludeTags = {
      title: '',
      type: 'object',
      properties: {
        active: {
          type: 'boolean',
          title: 'Specify tags to exclude (unchecked for none, overridden by includes).',
          default: false,
        },
      },
      dependencies: {
        active: {
          oneOf: [
            { properties: { active: { enum: [false] } } },
            {
              properties: {
                active: { enum: [true] },
                operator: {
                  type: 'string',
                  title: 'Filter type',
                  default: 'or',
                  oneOf: [
                    {
                      enum: ['and'],
                      title: 'All (files must not include all of the selected tags)',
                    },
                    {
                      enum: ['or'],
                      title: 'Any (files must not include any of the selected tags)',
                    },
                  ],
                },
                tags: {
                  isMulti: true,
                  type: 'array',
                  title: 'Select tags for this filter.',
                  uniqueItems: true,
                  default: [],
                  items: {
                    type: 'string',
                    anyOf: fileTagOptions,
                  },
                },
              },
            },
          ],
        },
      },
    };
  }

  return filters;
};

const getFilesPanelSchema = (types, tags) => {
  const filterProperties = getFilters(types, tags);

  return {
    title: {
      type: 'string',
      default: 'Files',
    },
    options: {
      type: 'object',
      properties: {
        layouts: {
          title: 'Layouts',
          type: 'string',
          default: 'Grid & list',
          enum: ['Grid & list', 'List & grid', 'Only grid', 'Only list'],
        },
        hideToolbar: {
          title: 'Hide toolbar',
          type: 'boolean',
          default: false,
        },
        fileLimit: {
          title: 'Limit files per page',
          type: 'number',
          default: 20,
        },
        hideSearch: {
          title: 'Hide search input',
          type: 'boolean',
          default: false,
        },
        hideFilters: {
          title: 'Hide tag & type filters',
          type: 'boolean',
          default: false,
        },
        hideUploadButton: {
          title: 'Hide upload button',
          type: 'boolean',
          default: false,
        },
        disableSelect: {
          title: 'Disable selecting of files',
          type: 'boolean',
          default: false,
        },
        showFileExtension: {
          title: 'Show file extensions',
          type: 'boolean',
          default: true,
        },
        filters: {
          title: 'Filters',
          type: 'object',
          properties: filterProperties,
        },
      },
    },
  };
};

const getFilesPanelAdminSchema = (types = [], tags = []) => {
  const filterProperties = getFilters(types, tags, false);

  return {
    type: 'object',
    properties: {
      filters: {
        title: 'Filters',
        description: Object.keys(filterProperties).length > 0 ? '' : 'None available',
        type: 'object',
        properties: filterProperties,
      },
    },
  };
};

export { getFilesPanelSchema, getFilesPanelAdminSchema };
